import { Injectable } from "@angular/core";
import {
    ConvertWeeklyDataDto,
    ManualEntryDto,
    ManualTimeServiceProxy,
    ProductivityBarServiceProxy,
    ProductivityChartDto, TimeAndAttendanceServiceProxy,
    UserActivityTempCreateOrEditDto
} from "@shared/service-proxies/service-proxies";
import { forkJoin, Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { ProductivityCandle } from "../models/productivity-candle";
import * as _ from "lodash";
import { DateTime } from "luxon";
import { ProductivityCandleSection } from "../models/productivity-candle-section";
import { AbpSessionService } from "abp-ng2-module";
import { ActivityService } from "./activity.service";

export interface LiveDayProductivityBarRow {
    date: DateTime;
    productivityData: ProductivityCandle[];
    manualTime: ManualEntryDto[];
    trackedTime: string;
    productive: number;
    unProductive: number;
    nuetral: number;
}

@Injectable()
export class ProductivityBarService {
    viewDate: Date;
    constructor(private _timeAndAttendanceServiceProxy: TimeAndAttendanceServiceProxy, private _abpSessionService: AbpSessionService, private _productivityBarServiceProxy: ProductivityBarServiceProxy,
                    private _manualTimeServiceProxy: ManualTimeServiceProxy,
                    private _activityService: ActivityService) { }

    // store for manual entry dto
    manuallyAddedData: ManualEntryDto[];

    // Method to fetch productivity bar data and modify it according to component input
    getProductivityBarData(
        userId: number,
        tenantId: number,
        timeSegment: number,
        startTime: number,
        endTime: number,
        date?: Date
    ): Observable<ProductivityCandle[]> {
        let startDate;
        let endDate;
        date = date ? date : this.viewDate;
        if (!date) {
            startDate = new Date();
            endDate = new Date();
        } else {
            startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            endDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());;
        }
        startDate.setHours(startTime, 0, 0, 0);
        endDate.setHours(endTime, 1, 0, 0);
        this.setViewDate(new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()));
        const calendarDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isSelectedDateToday = calendarDateOnly.getTime() === today.getTime();
        const apiSelectedDate = this._activityService.toApiCalendarDate(calendarDateOnly);
        const apiCurrentDate = isSelectedDateToday
            ? this._activityService.toApiCalendarDate(new Date())
            : apiSelectedDate;
        const timeZoneOffset = isSelectedDateToday
            ? (new Date()).getTimezoneOffset()
            : calendarDateOnly.getTimezoneOffset();
        return this._productivityBarServiceProxy
            .getProductivityBarInfo(userId, tenantId, timeSegment, apiSelectedDate, DateTime.fromJSDate(endDate), false, timeZoneOffset, apiCurrentDate, false)
            .pipe(
                map((r) => {
                    this.manuallyAddedData = r.manualAddEntryDto ?? [];
                    return this.mapBarDtoToCandles(
                        r.productivityBarDto,
                        r.manualAddEntryDto ?? [],
                        startDate,
                        startTime,
                        endTime,
                        timeSegment
                    );
                })
            );
    }

    loadLivePeriodDayBars(
        userId: number,
        tenantId: number,
        days: Date[],
        startTime: number,
        endTime: number,
        timeSegment: number
    ): Observable<LiveDayProductivityBarRow[]> {
        if (!days?.length) {
            return of([]);
        }
        const requests = days.map((day) =>
            this.fetchLiveDayProductivityBar(userId, tenantId, day, startTime, endTime, timeSegment).pipe(
                catchError(() => of(this.buildEmptyLiveDayRow(day, startTime, endTime, timeSegment)))
            )
        );
        return forkJoin(requests);
    }

    buildNvd3FromLiveDayRows(rows: LiveDayProductivityBarRow[]): {
        nvd3Array: { key: string; values: number[][]; color?: string }[];
        productivityNoDataFound: boolean;
    } {
        const chartPoints = rows.map((row) => {
            const dayJs = row.date.toJSDate();
            dayJs.setHours(12, 0, 0, 0);
            return {
                time: dayJs.getTime(),
                productive: row.productive,
                unProductive: row.unProductive,
                neutral: row.nuetral,
            };
        });
        const hasChartData = chartPoints.some(
            (p) => p.productive > 0 || p.unProductive > 0 || p.neutral > 0
        );
        if (!hasChartData) {
            return { nvd3Array: [{ key: "No Data Found", values: [] }], productivityNoDataFound: true };
        }
        const labels = ["productive", "unProductive", "neutral"] as const;
        const colorArray = ["#48C3FC", "#F64E60", "#E4E4E4"];
        const nvd3Array = labels.map((label, i) => ({
            color: colorArray[i],
            key: label === "unProductive" ? "UnProductive" : label.charAt(0).toUpperCase() + label.slice(1),
            values: chartPoints.map((p) => [p.time, Number(p[label]) || 0]),
        }));
        return { nvd3Array, productivityNoDataFound: false };
    }

    private fetchLiveDayProductivityBar(
        userId: number,
        tenantId: number,
        day: Date,
        startTime: number,
        endTime: number,
        timeSegment: number
    ): Observable<LiveDayProductivityBarRow> {
        const calendarDateOnly = new Date(day.getFullYear(), day.getMonth(), day.getDate());
        const startDate = new Date(calendarDateOnly);
        startDate.setHours(startTime, 0, 0, 0);
        const endDate = new Date(calendarDateOnly);
        endDate.setHours(endTime, 1, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isSelectedDateToday = calendarDateOnly.getTime() === today.getTime();
        const apiSelectedDate = this._activityService.toApiCalendarDate(calendarDateOnly);
        const apiCurrentDate = isSelectedDateToday
            ? this._activityService.toApiCalendarDate(new Date())
            : apiSelectedDate;
        const timeZoneOffset = isSelectedDateToday
            ? new Date().getTimezoneOffset()
            : calendarDateOnly.getTimezoneOffset();

        const bar$ = this._productivityBarServiceProxy
            .getProductivityBarInfo(
                userId,
                tenantId,
                timeSegment,
                apiSelectedDate,
                DateTime.fromJSDate(endDate),
                false,
                timeZoneOffset,
                apiCurrentDate,
                false
            )
            .pipe(catchError(() => of(null)));

        const stats$ = this._timeAndAttendanceServiceProxy
            .getUserCalculationBaseOnActivity(
                userId,
                tenantId,
                apiSelectedDate,
                timeZoneOffset,
                apiSelectedDate,
                apiSelectedDate,
                undefined
            )
            .pipe(catchError(() => of(null)));

        return forkJoin({ bar: bar$, stats: stats$ }).pipe(
            map(({ bar, stats }) => {
                const manual = bar?.manualAddEntryDto ?? [];
                const dtoList = bar?.productivityBarDto;
                const hasBarData = dtoList?.length > 0;
                const productivityData = hasBarData
                    ? this.mapBarDtoToCandles(dtoList, manual, startDate, startTime, endTime, timeSegment)
                    : this.buildEmptyCandleSlots(startTime, endTime);
                const mix = this.computeDayMixPercentages(productivityData);
                return {
                    date: DateTime.fromJSDate(calendarDateOnly),
                    productivityData,
                    manualTime: manual,
                    trackedTime: stats?.trackedTime ?? "00:00:00",
                    productive: mix.productive,
                    unProductive: mix.unProductive,
                    nuetral: mix.nuetral,
                };
            })
        );
    }

    private buildEmptyLiveDayRow(
        day: Date,
        startTime: number,
        endTime: number,
        _timeSegment: number
    ): LiveDayProductivityBarRow {
        const calendarDateOnly = new Date(day.getFullYear(), day.getMonth(), day.getDate());
        return {
            date: DateTime.fromJSDate(calendarDateOnly),
            productivityData: this.buildEmptyCandleSlots(startTime, endTime),
            manualTime: [],
            trackedTime: "00:00:00",
            productive: 0,
            unProductive: 0,
            nuetral: 0,
        };
    }

    private buildEmptyCandleSlots(startTime: number, endTime: number): ProductivityCandle[] {
        const candlesPerHour = 12;
        const expectedSlots = (endTime - startTime) * candlesPerHour;
        const prodArray: ProductivityCandle[] = [];
        for (let i = 0; i < expectedSlots; i++) {
            prodArray.push(
                new ProductivityCandle(
                    false,
                    0,
                    0,
                    0,
                    0,
                    [],
                    [],
                    [],
                    [],
                    false,
                    false,
                    new Date(
                        new Date().setHours(
                            startTime + Math.floor(i / candlesPerHour),
                            (i % candlesPerHour) * (60 / candlesPerHour),
                            0,
                            0
                        )
                    )
                )
            );
        }
        return prodArray;
    }

    private mapBarDtoToCandles(
        productivityBarDto: ProductivityChartDto[] | undefined,
        manualEntries: ManualEntryDto[],
        filterStartDate: Date,
        startTime: number,
        endTime: number,
        timeSegment: number
    ): ProductivityCandle[] {
        let filtered = productivityBarDto
            ? productivityBarDto.filter((x) => this.getLocalJsDate(x.startPointDate) >= filterStartDate)
            : [];
        filtered = _.uniqBy(filtered, (r) => r.startPointDate.hour * 60 + r.startPointDate.minute);
        let productivityCandleArray = filtered.map((dto) => this.mapToProductivityCandle(dto));
        const no_of_Candles = (endTime - startTime) * 12;
        if (productivityCandleArray.length < no_of_Candles) {
            for (let i = productivityCandleArray.length; i <= no_of_Candles; i++) {
                productivityCandleArray.push(new ProductivityCandle(false));
            }
        }
        const candlesPerHour = 12;
        const startTimeHour = startTime;
        const endTimeHour = endTime;
        const expectedSlots = (endTimeHour - startTimeHour) * candlesPerHour;
        const initalCandles = startTime * candlesPerHour;
        const lastCandle = endTime * candlesPerHour;
        const x = this.applyManualTime(productivityCandleArray, startTime, endTime, timeSegment, manualEntries);
        const sourceCandles = x.length >= lastCandle ? x.slice(initalCandles, lastCandle) : x;
        const prodArray: ProductivityCandle[] = [];
        sourceCandles.forEach((item) => {
            if (!item?.date) {
                return;
            }
            const date = new Date(item.date);
            const hour = date.getHours();
            const minute = date.getMinutes();
            const timeIndex =
                (hour - startTimeHour) * candlesPerHour + Math.floor(minute / (60 / candlesPerHour));
            if (timeIndex >= 0 && timeIndex < expectedSlots) {
                prodArray[timeIndex] = item;
            }
        });
        for (let i = 0; i < expectedSlots; i++) {
            if (!prodArray[i]) {
                prodArray[i] = new ProductivityCandle(
                    false,
                    0,
                    0,
                    0,
                    0,
                    [],
                    [],
                    [],
                    [],
                    false,
                    false,
                    new Date(
                        new Date().setHours(
                            startTimeHour + Math.floor(i / candlesPerHour),
                            (i % candlesPerHour) * (60 / candlesPerHour),
                            0,
                            0
                        )
                    )
                );
            }
        }
        return prodArray;
    }

    private computeDayMixPercentages(candles: ProductivityCandle[]): {
        productive: number;
        unProductive: number;
        nuetral: number;
    } {
        const withData = candles.filter((c) => c.isDataAvailable);
        if (!withData.length) {
            return { productive: 0, unProductive: 0, nuetral: 0 };
        }
        const n = withData.length;
        return {
            productive: Math.round(withData.reduce((a, c) => a + (c.productivePercent || 0), 0) / n),
            unProductive: Math.round(withData.reduce((a, c) => a + (c.unproductivePercent || 0), 0) / n),
            nuetral: Math.round(withData.reduce((a, c) => a + (c.neutralPercent || 0), 0) / n),
        };
    }
    setZeroTimeForLocalDate(date: Date): Date {
        date.setHours(0, 0, 0, 0);
        return new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
    }
    // api call for adding manual entry time
    addManualEntry(id:number, minutes: number[], description: string, projectId: number, taskId: number, type: ProductivityCandleSection, selectedDate:Date,selectedTaskCategoryId:number): Observable<void> {
        const userActivityTempCreateOrEditDto = new UserActivityTempCreateOrEditDto();
        userActivityTempCreateOrEditDto.description = description;
        userActivityTempCreateOrEditDto.projectId = projectId;
        userActivityTempCreateOrEditDto.userProjectTaskId = taskId;
        userActivityTempCreateOrEditDto.userId = this._abpSessionService.userId;
        userActivityTempCreateOrEditDto.tenantId = this._abpSessionService.tenantId;
        userActivityTempCreateOrEditDto.productivityTypeId = type;
        userActivityTempCreateOrEditDto.startDateTime = this.minutesToUTCDateTime(minutes[0]);
        userActivityTempCreateOrEditDto.endDateTime = this.minutesToUTCDateTime(minutes[1]);
        userActivityTempCreateOrEditDto.dateAppliedFor = DateTime.fromJSDate(this.setZeroTimeForLocalDate(selectedDate));     
        userActivityTempCreateOrEditDto.taskCategoryId = selectedTaskCategoryId;   
        return this._manualTimeServiceProxy.addManualEntry(userActivityTempCreateOrEditDto);
    }

    applyManualTime(
        productivityCandleArray: ProductivityCandle[],
        startTimeHour: number,
        endTimeHour: number,
        timeSegment: number,
        manualEntries?: ManualEntryDto[]
    ): ProductivityCandle[] {
        let x = productivityCandleArray;
        const slots = manualEntries ?? this.manuallyAddedData ?? [];
        slots.forEach((manuallyAddedSlot) => {
            let startJsDate = this.getLocalJsDate(manuallyAddedSlot.startTime);
            let endJsDate = this.getLocalJsDate(manuallyAddedSlot.endTime);
            let startMinutes = startJsDate.getHours() * 60 + startJsDate.getMinutes();
            let endMinutes = endJsDate.getHours() * 60 + endJsDate.getMinutes() - timeSegment;
            const candlesBeforeStartHour = (60 * startTimeHour) / timeSegment;
            let startIndex = Math.round(candlesBeforeStartHour + ((startMinutes - startTimeHour * 60) / timeSegment - 1));
            startIndex = startIndex<0? 0:startIndex;
            let endIndex = Math.round(candlesBeforeStartHour + ((endMinutes - startTimeHour * 60) / timeSegment - 1));
            for (let i = startIndex; i <= endIndex; i++) {
                if (x[i].isDataAvailable != undefined) {
                    x[i].isDataAvailable = true;
                }
                x[i].isManualTime = true;
                x[i].isApproved = manuallyAddedSlot.isApproved;
                x[i].date = this.getCandleDateFromIndex(i, timeSegment);
                x[i].TotalMinutes = timeSegment;
                if (manuallyAddedSlot.productivityType === 1) {
                    x[i].makeAllProductive(manuallyAddedSlot.description);
                } else if (manuallyAddedSlot.productivityType === 2) {
                    x[i].makeAllUnproductive(manuallyAddedSlot.description);
                } else if (manuallyAddedSlot.productivityType === 3) {
                    x[i].makeAllNeutral(manuallyAddedSlot.description);
                }
            }
        });
        return x;
    }

    // Method to map a ProductivityChartDto -> ProductivityCandle
    mapToProductivityCandle(productivityChartDto: ProductivityChartDto): ProductivityCandle {
        let isDataAvailable: boolean = true;
        if (_.every(Object.values(productivityChartDto.dataChart), (x) => x === 0)) isDataAvailable = false;
        return new ProductivityCandle(
            isDataAvailable,
            productivityChartDto.dataChart.productive,
            productivityChartDto.dataChart.unProductive,
            productivityChartDto.dataChart.neutral,
            productivityChartDto.dataChart.private,
            productivityChartDto.productivityInfo,
            productivityChartDto.unProductivityInfo,
            productivityChartDto.neutralInfo,
            productivityChartDto.privateTimeInfo,
            false,
            false,
            this.getLocalJsDate(productivityChartDto.startPointDate)
        );
    }

    // Utility function
    calculateTotalSeconds(timeStamp: string): number {
        return timeStamp
            .split(":")
            .map((r) => parseInt(r))
            .map((r, i) => r * 60 ** (2 - i))
            .reduce((a, v) => (a += v), 0);
    }

    getLocalJsDate(date: DateTime): Date {
        let jsDate = new Date(
            `${date.year}-${date.month < 10 ? "0" + date.month : date.month}-${date.day < 10 ? "0" + date.day : date.day}T${
                date.hour < 10 ? "0" + date.hour : date.hour
            }:${date.minute < 10 ? "0" + date.minute : date.minute}:00`
        );
        return new Date(jsDate.getTime() - jsDate.getTimezoneOffset() * 60 * 1000);
    }

    getLocalTimeStamp(date: DateTime): string {
        let localDate = this.getLocalJsDate(date);
        return localDate.toTimeString().split(" ")[0];
    }

    minutesToUTCDateTime(minutes: number): DateTime {
        let date = new Date();
        date.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
        date.setFullYear(this.viewDate.getFullYear(), this.viewDate.getMonth(),
            this.viewDate.getDate())
        return DateTime.fromJSDate(date);
    }

    secondsToDate(timeInSeconds: number): Date {
        const hours = Math.floor(timeInSeconds / 3600);
        let minutes = Math.floor((timeInSeconds - hours * 3600) / 60);
        let seconds = timeInSeconds - hours * 3600 - minutes * 60;
        let date = new Date(new Date().toLocaleString());
        date.setHours(hours, minutes, seconds, 0);
        return date;
    }

    getDummyDateForWeeklyView(userId: number, tenantId: number, passMinute: number, filterStartDate: DateTime, filterEndDate: DateTime, isBagroundJobCall: boolean): Observable<ConvertWeeklyDataDto[]> {
        const currDate = this._activityService.toApiCalendarDate(new Date());
        return this._productivityBarServiceProxy.getWeeklyProductiveBarData(userId, tenantId, passMinute, filterStartDate, filterEndDate, isBagroundJobCall, (new Date()).getTimezoneOffset(), currDate, false).pipe(map((r) => {
            if (r) return r.slice(0,132)
            if (!r) return []
        }));
    }

    getDummyDateForMonthlyView(): Observable<ConvertWeeklyDataDto[]> {
        return this._productivityBarServiceProxy.getMonthlyProductivityBarData().pipe(
            map((r) => r.slice(0, 132))
        );
    }

    deleteManualTime(id: number): Observable<void> {
        return this._manualTimeServiceProxy.deleteManualEntry(id);
    }

    getManualTime(): ManualEntryDto[] {
        return this.manuallyAddedData;
    }

    jsDateToMinutesInTheDay(date: Date): number {
        return date.getHours() * 60 + date.getMinutes();
    }

    setViewDate(date: Date){
      
        this.viewDate = date;
    }

    /** Maps a midnight-based candle index to the local start time of that 5-minute segment. */
    private getCandleDateFromIndex(index: number, timeSegment: number): Date {
        const minutesFromMidnight = (index + 1) * timeSegment;
        const baseDate = this.viewDate ? new Date(this.viewDate) : new Date();
        baseDate.setHours(0, 0, 0, 0);
        baseDate.setHours(Math.floor(minutesFromMidnight / 60), minutesFromMidnight % 60, 0, 0);
        return baseDate;
    }
}
